import { TIMEZONE } from '../../constants';
import type { StormEvent } from '../../../types/report';
import './StormTable.css';

/**
 * Gusts below this at an event's peak mean high wind cannot explain it. The
 * threshold matches the band where outage risk actually starts climbing.
 */
const WINDY_GUST_KMH = 70;

function formatStart(iso: string): string {
  return new Date(iso).toLocaleString('en-CA', {
    timeZone: TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function StormTable({ storms }: { storms: StormEvent[] }) {
  return (
    <figure className="StormTable">
      <figcaption className="StormTable-caption">
        The ten largest outage events by peak customers affected, with the wind and
        pressure recorded across the island when each one peaked.
      </figcaption>
      <div className="StormTable-scroll">
        <table>
          <thead>
            <tr>
              <th scope="col">Began</th>
              <th scope="col">Hours</th>
              <th scope="col">Peak customers</th>
              <th scope="col">Communities</th>
              <th scope="col">Hardest hit</th>
              <th scope="col">Gust</th>
              <th scope="col">Pressure</th>
            </tr>
          </thead>
          <tbody>
            {storms.map((s) => {
              const calm = s.gustAtPeakKmh !== null && s.gustAtPeakKmh < WINDY_GUST_KMH;
              return (
                <tr key={s.startedAt}>
                  <td>{formatStart(s.startedAt)}</td>
                  <td>{s.hours ?? '—'}</td>
                  <td>{s.peakCustomersOut.toLocaleString('en-CA')}</td>
                  <td>{s.peakCommunitiesAffected}</td>
                  <td>{s.topCounty ?? '—'}</td>
                  <td className={calm ? 'StormTable-calm' : undefined}>
                    {s.gustAtPeakKmh === null ? '—' : `${s.gustAtPeakKmh} km/h`}
                  </td>
                  <td>{s.pressureAtPeakKpa === null ? '—' : `${s.pressureAtPeakKpa} kPa`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="StormTable-footnote">
        Highlighted gusts were below {WINDY_GUST_KMH} km/h — the level at which outage
        risk begins to climb. Those events were not driven by high wind.
      </p>
    </figure>
  );
}
